import { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Plus, Minus, HelpCircle, Lightbulb, Sparkles } from 'lucide-react';
import type { MindmapNode } from './mockData';
import { getNodeColors } from './mockData';
import { AITypingParagraph } from './AITypingEffect';

interface NodeDetailModalProps {
  node: MindmapNode | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Deep-dive modal for detailed node content.
 * Shows tradeoffs, questions, and vendor insights.
 */
export function NodeDetailModal({ node, isOpen, onClose }: NodeDetailModalProps) {
  // Close on escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!node) return null;

  const colors = getNodeColors(node.id);
  const detail = node.detailContent;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[85vh] bg-[#063F6B] rounded-2xl border border-[#1a6394] shadow-2xl z-50 overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className={`relative bg-gradient-to-r ${colors.gradient} p-6`}>
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              <div className="pr-12">
                <h2 className="text-2xl font-bold text-white mb-1">{node.title}</h2>
                {node.subtitle && (
                  <p className="text-white/70">{node.subtitle}</p>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Main content */}
              <div className="bg-[#042a4d] rounded-xl p-5 border border-[#1a6394]">
                <div className="flex items-center gap-2 text-amber-400 text-sm mb-3">
                  <Sparkles className="w-4 h-4" />
                  <span>AI Analysis</span>
                </div>
                <p className="text-slate-200 leading-relaxed">{node.content}</p>
              </div>

              {/* Tradeoffs */}
              {detail?.tradeoffs && detail.tradeoffs.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <div className="w-1 h-5 bg-amber-500 rounded-full" />
                    Key Tradeoffs
                  </h3>
                  <div className="space-y-3">
                    {detail.tradeoffs.map((tradeoff, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-[#042a4d] rounded-xl p-4 border border-[#1a6394]"
                      >
                        <div className="grid md:grid-cols-2 gap-4">
                          {/* Gain */}
                          <div className="flex gap-3">
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                              <Plus className="w-4 h-4 text-emerald-400" />
                            </div>
                            <div>
                              <div className="text-xs text-emerald-400 font-medium uppercase mb-1">
                                You Gain
                              </div>
                              <p className="text-sm text-slate-300">{tradeoff.gain}</p>
                            </div>
                          </div>
                          {/* Give Up */}
                          <div className="flex gap-3">
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-rose-500/20 flex items-center justify-center">
                              <Minus className="w-4 h-4 text-rose-400" />
                            </div>
                            <div>
                              <div className="text-xs text-rose-400 font-medium uppercase mb-1">
                                You Give Up
                              </div>
                              <p className="text-sm text-slate-300">{tradeoff.giveUp}</p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Questions */}
              {detail?.questions && detail.questions.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <div className="w-1 h-5 bg-amber-500 rounded-full" />
                    Questions to Consider
                  </h3>
                  <div className="bg-amber-500/10 rounded-xl p-5 border border-amber-500/30">
                    <div className="space-y-3">
                      {detail.questions.map((question, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 + index * 0.1 }}
                          className="flex gap-3"
                        >
                          <HelpCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                          <p className="text-slate-200">{question}</p>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Vendor Insights */}
              {detail?.vendorInsights && detail.vendorInsights.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <div className="w-1 h-5 bg-amber-500 rounded-full" />
                    Based on Your Evaluation
                  </h3>
                  <div className="bg-[#042a4d] rounded-xl p-5 border border-[#1a6394]">
                    <div className="flex items-center gap-2 text-amber-400 text-sm mb-4">
                      <Lightbulb className="w-4 h-4" />
                      <span>Personalized Insights</span>
                    </div>
                    <div className="space-y-3">
                      {detail.vendorInsights.map((insight, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.5 + index * 0.15 }}
                          className="flex gap-3 items-start"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
                          <p className="text-slate-300">{insight}</p>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-[#1a6394] bg-[#042a4d]">
              <button
                onClick={onClose}
                className="w-full py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-medium rounded-xl transition-all shadow-lg hover:shadow-xl"
              >
                Close Deep Dive
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
